// Полноэкранный режим браузера: прячет адресную строку и кнопки браузера.
// Браузеры разрешают его только по жесту игрока, поэтому включаем при первом касании в игре
// (один раз за сессию — если игрок вышел из полного экрана сам, не навязываем снова).
// iPhone Safari Fullscreen API для страниц не поддерживает — там помогает «На экран Домой» (PWA).

type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
};
type FsElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };

const SESSION_KEY = 'sk_fs_auto';

export function fullscreenSupported(): boolean {
  const el = document.documentElement as FsElement;
  return !!(el.requestFullscreen || el.webkitRequestFullscreen);
}

export function isFullscreen(): boolean {
  const d = document as FsDocument;
  return !!(d.fullscreenElement || d.webkitFullscreenElement);
}

/** Игра запущена как установленное приложение — браузерных панелей и так нет. */
function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export async function enterFullscreen(): Promise<void> {
  if (isFullscreen() || !fullscreenSupported()) return;
  const el = document.documentElement as FsElement;
  try {
    if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
    else await el.webkitRequestFullscreen?.();
  } catch {
    // Браузер отказал (нет жеста, запрет в настройках) — играем в обычном режиме
  }
}

export async function exitFullscreen(): Promise<void> {
  const d = document as FsDocument;
  try {
    if (d.exitFullscreen) await d.exitFullscreen();
    else await d.webkitExitFullscreen?.();
  } catch {
    // уже вышли
  }
}

export async function toggleFullscreen(): Promise<void> {
  if (isFullscreen()) await exitFullscreen();
  else await enterFullscreen();
}

/** Включить полный экран при первом касании в игре (вызывается из игрового layout). */
export function autoFullscreenOnFirstTap(): () => void {
  if (isStandalone() || !fullscreenSupported() || sessionStorage.getItem(SESSION_KEY)) return () => {};
  const onTap = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    window.removeEventListener('pointerup', onTap, true);
    void enterFullscreen();
  };
  window.addEventListener('pointerup', onTap, true);
  return () => window.removeEventListener('pointerup', onTap, true);
}
